import React, { Suspense, useEffect } from "react";
import { AppShell, buildTheme, cssVariables, dowtex, useColorMode } from "@dowty/ui";
import { App as AntApp, Badge, ConfigProvider } from "antd";
import {
    AppstoreOutlined,
    LogoutOutlined,
    MoonFilled,
    SunFilled,
    TeamOutlined,
    UserOutlined,
    VideoCameraOutlined,
} from "@ant-design/icons";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useNavigate,
    useLocation,
} from "react-router-dom";
import "@dowty/ui/base.css";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import Dashboard, { getAppUrl } from "./pages/Dashboard";
import ProfilePage from "./pages/ProfilePage";
import FriendsPage from "./pages/FriendsPage";
import QuickAddPage from "./pages/QuickAddPage";
import DualcamAlbumsPage from "./dualcam/DualcamAlbumsPage";
import DualcamAlbumPage from "./dualcam/DualcamAlbumPage";
import DualcamMapPage from "./dualcam/DualcamMapPage";
import DualcamStatsPage from "./dualcam/DualcamStatsPage";
import PublicSharePage from "./dualcam/PublicSharePage";
import ShowcasePage from "./showcase/ShowcasePage";
import { useOutbox, useOutboxSync } from "@dowty/offline";
import { useFollowGraph } from "./hooks/useFollowGraph";
import { outbox } from "./offline/offline";
import { AppLegalFooter, AppLegalPage, LegalRoutePage, NotFoundPage } from "./legal";

const TournamentShareRoute = React.lazy(() => import("./tournament/TournamentShareRoute"));
const NAV_ITEMS = [
    { href: "/dashboard", icon: <AppstoreOutlined />, label: "Home" },
    { href: "/friends", icon: <TeamOutlined />, label: "People" },
    { href: "/dualcam", icon: <VideoCameraOutlined />, label: "DualCam" },
    { href: "/profile", icon: <UserOutlined />, label: "Profile" },
];

const AppRoutes: React.FC = () => (
    <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/drinks" element={null} />
        <Route path="/quick-add" element={<QuickAddPage />} />
        <Route path="/dualcam" element={<DualcamAlbumsPage />} />
        <Route path="/dualcam/:albumId" element={<DualcamAlbumPage />} />
        <Route path="/dualcam/:albumId/map" element={<DualcamMapPage />} />
        <Route path="/dualcam/:albumId/stats" element={<DualcamStatsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
    </Routes>
);

const Brand = () => <div className="sf-brand">
    <img src="/logo192.png" alt="" />
    <span>Dowtex</span>
</div>;

const AuthenticatedLayout: React.FC<{ mode: "light" | "dark"; toggleTheme: () => void }> = ({ mode, toggleTheme }) => {
    const { isAuthenticated, isLoading, logout, user } = useAuth();
    const { message } = AntApp.useApp();
    const location = useLocation();
    const navigate = useNavigate();
    const { pendingCount } = useOutbox(outbox);
    const { incoming: followRequests } = useFollowGraph();
    useOutboxSync(outbox);

    useEffect(() => {
        if (!isLoading && isAuthenticated && location.pathname === "/drinks") {
            window.location.replace(getAppUrl("saufdings"));
        }
    }, [isLoading, isAuthenticated, location.pathname]);

    useEffect(() => {
        const handleShellNavigation = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            const anchor = target.closest<HTMLAnchorElement>(".ui-shell a[href], .ui-mobile-more a[href]");
            if (anchor?.origin !== window.location.origin || (anchor.pathname === location.pathname && Boolean(anchor.hash))) return;
            event.preventDefault();
            navigate(anchor.pathname + anchor.search + anchor.hash);
        };
        document.addEventListener("click", handleShellNavigation, true);
        return () => document.removeEventListener("click", handleShellNavigation, true);
    }, [location.pathname, navigate]);

    if (isLoading) return null;
    if (!isAuthenticated) return <Navigate to="/login" replace />;

    const isDark = mode === "dark";
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const signOut = async () => {
        try { await logout(); navigate("/login"); }
        catch { message.error(isGerman ? "Abmeldung fehlgeschlagen. Bitte erneut versuchen." : "Sign out failed. Please retry."); }
    };
    const badge = (icon: React.ReactNode, count: number) => count > 0 ? <Badge count={count}>{icon}</Badge> : icon;

    return <AppShell
        items={NAV_ITEMS.map((item) => ({
            ...item,
            icon: badge(item.icon, ({ "/friends": followRequests.length, "/profile": pendingCount } as Record<string, number>)[item.href] ?? 0),
        }))}
        currentPath={location.pathname}
        brand={<Brand />}
        account={{
            name: user?.username ?? "",
            menuLabel: isGerman ? "Kontomenü" : "Account menu",
            actions: [
                { key: "theme", label: isGerman ? "Design" : "Theme", icon: isDark ? <SunFilled /> : <MoonFilled />, onSelect: toggleTheme },
                { key: "logout", label: isGerman ? "Abmelden" : "Log out", icon: <LogoutOutlined />, onSelect: signOut },
            ],
        }}
        legal={<AppLegalFooter />}
    >
        <div className="sf-page-content"><AppRoutes /></div>
    </AppShell>;
};

const App: React.FC = () => <DowtexPortal />;

const DowtexPortal: React.FC = () => {
    const { mode, setSetting } = useColorMode("theme", dowtex);

    useEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = mode;
        root.style.colorScheme = mode;
        Object.entries(cssVariables(dowtex, mode)).forEach(([key, value]) => root.style.setProperty(key, value));
    }, [mode]);

    const toggleTheme = () => setSetting(mode === "dark" ? "light" : "dark");

    return <ConfigProvider theme={buildTheme(dowtex, mode)}><AntApp>
        <Router>
            <AuthProvider>
                <Routes>
                    <Route path="/" element={<AppLegalPage><ShowcasePage /></AppLegalPage>} />
                    <Route path="/login" element={<AppLegalPage><LoginPage /></AppLegalPage>} />
                    <Route path="/impressum" element={<LegalRoutePage page="impressum" />} />
                    <Route path="/datenschutz" element={<LegalRoutePage page="privacy" />} />
                    <Route path="/s/:shareToken" element={<PublicSharePage />} />
                    <Route path="/t/:shareToken" element={<AppLegalPage><Suspense fallback={null}><TournamentShareRoute /></Suspense></AppLegalPage>} />
                    <Route path="/*" element={<AuthenticatedLayout mode={mode} toggleTheme={toggleTheme} />} />
                </Routes>
            </AuthProvider>
        </Router>
    </AntApp></ConfigProvider>;
};

export default App;
